import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from '../../../../../../../../ui/toast';
import {
  createAutoBuildQuestions,
  getAutoAssessment,
  setAutoAssessmentSkills,
} from '../../../../../../../../utils/api_service/JobOpenings';
import { RootState } from '../../../../../../../../utils/redux_store/store';
import {
  AutoAssessment,
  AutoBuildCompleteStatus,
  JobOpeningSkill,
} from '../../../../../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../../../../../utils/types/react_select_types';

interface IUseQuickBuild {
  skills: string[];
  autoAssessment: AutoAssessment;
  setAutoAssessment: React.Dispatch<React.SetStateAction<AutoAssessment>>;
  jobTitle: string;
  completedElement?: () => void;
  onGenerationComplete?: () => void;
}

interface IQuickBuildSkill extends JobOpeningSkill {
  checked?: boolean;
}

export const useQuickBuild = (props: IUseQuickBuild) => {
  const {
    skills,
    autoAssessment,
    setAutoAssessment,
    jobTitle,
    completedElement,
    onGenerationComplete,
  } = props;
  const { id } = useParams<{ id: string }>();
  const [records, setRecords] = useState<IQuickBuildSkill[]>(
    autoAssessment?.skills || [],
  );
  const [skillsList, setSkillsList] = useState<string[]>(
    skills.filter(
      (skill) =>
        !autoAssessment?.skills?.some(
          (iteratedSkill) => iteratedSkill.skill === skill,
        ),
    ),
  );
  const [generatingQuestions, setGeneratingQuestions] =
    useState<boolean>(false);
  const [editedJobTitle, setEditedJobTitle] = useState<string>(jobTitle);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const levels = useSelector((state: RootState) => state.skillLevels.levelList);

  const manualGenerationRef = useRef(false);
  const isPollingRef = useRef(false);

  const questionTypesList: SelectOptions[] = [
    {
      value: 'Modern',
      label: 'Video',
    },
    {
      value: 'Classic',
      label: 'Text',
    },
  ];

  const pollJobStatus = useCallback(() => {
    if (isPollingRef.current) return;
    isPollingRef.current = true;

    let isCancelled = false;

    const poll = () => {
      getAutoAssessment(id)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          setAutoAssessment(data);
          if (!isCancelled) {
            if (
              data.status === AutoBuildCompleteStatus ||
              data.status === null
            ) {
              const wasGenerating = manualGenerationRef.current;
              manualGenerationRef.current = false;
              isPollingRef.current = false;
              setGeneratingQuestions(false);

              // Always refresh questions if complete
              if (onGenerationComplete) {
                onGenerationComplete();
              }

              // Only toast if user explicitly clicked
              if (wasGenerating) {
                toast.success('Questions Generated Successfully', {
                  position: 'bottom-center',
                });
              }
            } else {
              setTimeout(poll, 5000);
            }
          }
        })
        .catch((error) => {
          if (!isCancelled) {
            toast.error(
              `An error occurred while polling the job status - ${error}`,
              { position: 'bottom-center' },
            );
            isPollingRef.current = false;
            manualGenerationRef.current = false;
            setGeneratingQuestions(false);
          }
        });
    };

    poll();

    return () => {
      isCancelled = true;
      isPollingRef.current = false;
    };
  }, [id, onGenerationComplete]);

  useEffect(() => {
    if (
      autoAssessment?.status &&
      autoAssessment?.status !== AutoBuildCompleteStatus
    ) {
      setGeneratingQuestions(true);
      const cleanup = pollJobStatus();
      return cleanup;
    } else {
      setGeneratingQuestions(false);
    }
  }, [id, pollJobStatus, autoAssessment?.status]);

  const addNewSkillRow = () => {
    setRecords((prevRecords) => [
      ...prevRecords,
      {
        skill: '',
        level: records.length > 0 ? records[0].level : levels[0].value,
        numberOfQuestions:
          records.length > 0 ? records[0].numberOfQuestions : 5,
        points: records.length > 0 ? records[0].points : 1,
        time: records.length > 0 ? records[0].time : 30,
        questionType: records.length > 0 ? records[0].questionType : 'Modern',
      },
    ]);
    setHasUnsavedChanges(true); // Mark as unsaved
  };

  const handleInputChange =
    (index: number, prop: keyof JobOpeningSkill) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setRecords((prevRecords) =>
          prevRecords.map((record, i) =>
            i === index ? { ...record, [prop]: event.target.value } : record,
          ),
        );
        setHasUnsavedChanges(true); // Mark as unsaved
      };

  const handleSelectChange =
    (index: number, prop: keyof JobOpeningSkill) =>
      (selectedOption: SelectOptions) => {
        setRecords((prevRecords) =>
          prevRecords.map((record, i) =>
            i === index ? { ...record, [prop]: selectedOption.value } : record,
          ),
        );
        setHasUnsavedChanges(true); // Mark as unsaved
      };

  const handleRemoveSkill = (index: number) => {
    if (records[index].temp) {
      const skillToRemove = records[index];
      setRecords((prevRecords) => prevRecords.filter((_, i) => i !== index));
      setSkillsList((prevSkills) => [...prevSkills, skillToRemove.skill]);
    } else {
      setRecords((prevRecords) => prevRecords.filter((_, i) => i !== index));
    }
    setHasUnsavedChanges(true); // Mark as unsaved
  };

  const saveSkills = () => {
    setIsSaving(true);
    const recordsWithoutTemp = records.map(
      ({ temp, checked, ...record }) => record,
    );
    setAutoAssessmentSkills(id, recordsWithoutTemp)
      .then((response) => {
        if (response.ok) {
          toast.success('Skills Saved Successfully', {
            position: 'bottom-center',
          });
          setAutoAssessment({
            ...autoAssessment,
            skills: recordsWithoutTemp,
            status: undefined, // Clear completion to re-enable "Auto-Generate" button
          });
          setHasUnsavedChanges(false); // Mark as saved
        } else {
          toast.error('Error Saving Skills', { position: 'bottom-center' });
        }
        setIsSaving(false);
      })
      .catch(() => {
        toast.error('Error Saving Skills', { position: 'bottom-center' });
        setIsSaving(false);
      });
  };

  const generateQuestions = () => {
    createAutoBuildQuestions(autoAssessment.id, editedJobTitle)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        manualGenerationRef.current = true;
        setGeneratingQuestions(true);
        pollJobStatus();
        if (completedElement) {
          completedElement();
        }
      })
      .catch((error) => {
        toast.error(`Error Generating Questions - ${error}`, {
          position: 'bottom-center',
        });
      });
  };

  const handleAddSkill = (skill: string) => {
    setHasUnsavedChanges(true);
    setRecords((prevRecords) => [
      ...prevRecords,
      {
        skill: skill,
        level: records.length > 0 ? records[0].level : levels[0].value,
        numberOfQuestions:
          records.length > 0 ? records[0].numberOfQuestions : 5,
        points: records.length > 0 ? records[0].points : 1,
        time: records.length > 0 ? records[0].time : 30,
        questionType: records.length > 0 ? records[0].questionType : 'Modern',
        temp: true,
      },
    ]);
    setSkillsList((prevSkills) => prevSkills.filter((s) => s !== skill));
  };

  const handleToggleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setRecords((prev) => prev.map((r) => ({ ...r, checked: isChecked })));
  };

  const handleRemoveSelected = () => {
    setRecords((prev) => {
      const remaining = prev.filter((r) => !r.checked);
      // Restore removed temp skills to suggestions
      const removed = prev.filter((r) => r.checked && r.temp);
      if (removed.length > 0) {
        setSkillsList((prevSkills) => [
          ...prevSkills,
          ...removed.map((r) => r.skill),
        ]);
      }
      return remaining;
    });
    setHasUnsavedChanges(true);
  };

  const handleAddAllSuggestions = () => {
    const newRecords = skillsList.map((skill) => ({
      skill: skill,
      level: records.length > 0 ? records[0].level : levels[0].value,
      numberOfQuestions: records.length > 0 ? records[0].numberOfQuestions : 5,
      points: records.length > 0 ? records[0].points : 1,
      time: records.length > 0 ? records[0].time : 30,
      questionType: records.length > 0 ? records[0].questionType : 'Modern',
      temp: true,
      checked: false,
    }));
    setRecords((prev) => [...prev, ...newRecords]);
    setSkillsList([]);
    setHasUnsavedChanges(true);
  };

  return {
    records,
    isSaving,
    generatingQuestions,
    hasUnsavedChanges,
    skillsList,
    questionTypesList,
    levels,
    editedJobTitle,
    handleRemoveSelected,
    handleAddAllSuggestions,
    handleToggleSelectAll,
    handleAddSkill,
    generateQuestions,
    saveSkills,
    handleRemoveSkill,
    handleSelectChange,
    handleInputChange,
    addNewSkillRow,
    setRecords,
    setEditedJobTitle,
  };
};
